import type { GraphQLClient as GraphQLRequestClient } from "graphql-request";

import type { Config } from "@jti/fe-common";
import { getConfig, getSelectedRegionId } from "@jti/fe-common";
import { getUser } from "@jti/rest-api";

import { MagentoApiClient } from "./MagentoApiClient";
import { MagentoGraphQLRequestClient } from "./MagentoGraphQLRequestClient";

type GetGqlClientOptions = {
  isTrial?: boolean;
  useGet?: boolean;
  config?: Config;
};

export type RequestConfig = typeof GraphQLRequestClient.prototype.requestConfig;

const getGqlClient = ({
  isTrial = false,
  useGet = false,
  config = getConfig(),
}: GetGqlClientOptions = {}): MagentoGraphQLRequestClient => {
  const requestConfig: RequestConfig = {};
  const user = getUser();
  if (useGet) {
    requestConfig.method = "GET";
  }
  requestConfig.jsonSerializer = JSON; // https://github.com/jasonkuhrt/graphql-request/issues/354
  const headers: Record<string, string> = {
    Store: isTrial ? config["trials-store-view"] : config["default-store-view"],
  };

  if (user?.magento_token) {
    headers.Authorization = `Bearer ${user.magento_token}`;
  }

  const selectedRegionId = getSelectedRegionId();
  if (selectedRegionId) {
    headers["X-Selected-Region-Id"] = selectedRegionId;
  }

  requestConfig.headers = headers;

  return new MagentoGraphQLRequestClient(config.MAGENTO_SERVICE_URL, requestConfig);
};

/**
 * @deprecated Use MagentoApiClient from @jti/magento-api-v2 instead.
 */
const client = getGqlClient();

/**
 * @deprecated Use MagentoApiClient from @jti/magento-api-v2 instead.
 */
const getClient = getGqlClient({ useGet: true });

/**
 * @deprecated Use MagentoApiClient from @jti/magento-api-v2 instead.
 */
export const magento = new MagentoApiClient(client, getClient);

/**
 * @deprecated Use MagentoApiClient from @jti/magento-api-v2 instead.
 */
const trialClient = getGqlClient({ isTrial: true });

/**
 * @deprecated Use MagentoApiClient from @jti/magento-api-v2 instead.
 */
const trialGetClient = getGqlClient({ isTrial: true, useGet: true });

/**
 * @deprecated Use MagentoApiClient from @jti/magento-api-v2 instead.
 */
export const trialMagento = new MagentoApiClient(trialClient, trialGetClient);

/**
 * @deprecated Use MagentoApiClient from @jti/magento-api-v2 instead.
 */
export const getCurrentMagentoClient = (isTrial = false): MagentoApiClient => (isTrial ? trialMagento : magento);

/**
 * @deprecated Use MagentoApiClient from @jti/magento-api-v2 instead.
 */
export const createMagentoClient = ({
  isTrial = false,
  config,
}: {
  isTrial?: boolean;
  config?: Config;
}): MagentoApiClient => {
  const client = getGqlClient({ isTrial, config });
  const getClient = getGqlClient({ isTrial, useGet: true, config });
  return new MagentoApiClient(client, getClient);
};
